import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { sb } from '../modules/state';
import { Storage } from '../modules/state';
import { useAuth } from './AuthContext';
import { useStateContext } from './StateContext';
import { formatWeight, parseWeight, cmToInches, formatWeightWithUnit, getDeltaDisplay } from '../utils/weight';

interface WeightEntry {
  id: string;
  user_id: string;
  weight_kg: number;
  recorded_at: string;
  created_at: string;
}

export interface BodyMeasurement {
  id: string;
  user_id: string;
  neck_cm?: number;
  chest_cm?: number;
  waist_cm?: number;
  hips_cm?: number;
  upper_arms_cm?: number;
  thighs_cm?: number;
  recorded_at: string;
  notes?: string | null;
}

interface UnitPreferences {
  preferred_weight_unit: 'kg' | 'lbs';
  preferred_measure_unit: 'cm' | 'in';
}

interface WeightContextType {
  currentWeight: number | null;
  goalWeight: number | null;
  startWeight: number | null;
  entries: WeightEntry[];
  measurements: BodyMeasurement[];
  addEntry: (weight: number, recordedAt: string) => Promise<void>;
  addMeasurements: (measurements: Partial<BodyMeasurement>) => Promise<void>;
  updateGoal: (weight: number) => void;
  unit: 'kg' | 'lbs';
  unitPreferences: UnitPreferences | null;
  hasUnitPreferences: boolean;
  setUnitPreferences: (prefs: UnitPreferences) => void;
  showHistoricalEntries: boolean;
  setShowHistoricalEntries: (show: boolean) => void;
  formatWeight: (kg: number | null) => string;
  formatWeightWithUnit: (kg: number | null) => string;
  parseWeight: (str: string) => number | null;
  formatMeasurement: (cm: number | null | undefined) => number;
  getDeltaDisplay: (delta: number) => { text: string; class: string };
}

const WeightContext = createContext<WeightContextType | undefined>(undefined);

export function WeightProvider({ children }: { children: ReactNode }) {
  const [currentWeight, setCurrentWeight] = useState<number | null>(null);
  const [goalWeight, setGoalWeight] = useState<number | null>(null);
  const [startWeight, setStartWeight] = useState<number | null>(null);
  const [entries, setEntries] = useState<WeightEntry[]>([]);
  const [measurements, setMeasurements] = useState<BodyMeasurement[]>([]);
  const [unit, setUnit] = useState<'kg' | 'lbs'>('kg');
  const [unitPreferences, setUnitPreferences] = useState<UnitPreferences | null>(null);
  const [showHistoricalEntries, setShowHistoricalEntries] = useState<boolean>(false);
  const { currentUser } = useAuth();
  const { profile } = useStateContext();

  // Initialize from StateContext profile if available
  useEffect(() => {
    if (profile?.preferred_weight_unit) {
      setUnit(profile.preferred_weight_unit);
      setUnitPreferences({
        preferred_weight_unit: profile.preferred_weight_unit as 'kg' | 'lbs',
        preferred_measure_unit: profile.preferred_measure_unit as 'cm' | 'in'
      });
    }
  }, [profile?.preferred_weight_unit, profile?.preferred_measure_unit]);

  // Load entries from Supabase
  useEffect(() => {
    if (!currentUser || !sb) return;

    const fetchEntries = async () => {
      try {
        const { data, error } = await sb
          .from('weight_entries')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('recorded_at', { ascending: false });

        if (error) {
          console.error('[Weight] Failed to load entries:', error);
        } else {
          setEntries(data || []);
        }
      } catch (err) {
        console.error('[Weight] Error fetching entries:', err);
      }
    };

    const fetchMeasurements = async () => {
      try {
        const { data, error } = await sb
          .from('body_measurements')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('recorded_at', { ascending: false });

        if (error) {
          console.error('[Weight] Failed to load measurements:', error);
        } else {
          setMeasurements(data || []);
        }
      } catch (err) {
        console.error('[Weight] Error fetching measurements:', err);
      }
    };

    const fetchProfile = async () => {
      try {
        const { data } = await sb
          .from('profiles')
          .select('start_weight_kg, goal_weight_kg')
          .eq('id', currentUser.id)
          .single();

        if (data) {
          setStartWeight(data.start_weight_kg);
          setGoalWeight(data.goal_weight_kg);
        }
      } catch (err) {
        console.error('[Weight] Error fetching profile:', err);
      }
    };

    fetchEntries();
    fetchMeasurements();
    fetchProfile();
  }, [currentUser?.id]);

  // Load from localStorage if Supabase fails
  useEffect(() => {
    const savedWeight = Storage.get('currentWeight');
    const savedGoal = Storage.get('goalWeight');
    const savedStart = Storage.get('startWeight');

    if (savedWeight && !currentWeight) setCurrentWeight(savedWeight);
    if (savedGoal && !goalWeight) setGoalWeight(savedGoal);
    if (savedStart && !startWeight) setStartWeight(savedStart);
  }, []);

  const addEntry = async (weight: number, recordedAt: string) => {
    if (!currentUser || !sb) {
      // Fallback to localStorage
      Storage.set('currentWeight', weight);
      setCurrentWeight(weight);
      return;
    }

    try {
      const { data, error } = await sb
        .from('weight_entries')
        .insert({
          user_id: currentUser.id,
          weight_kg: weight,
          recorded_at: recordedAt
        })
        .select()
        .single();

      if (error) {
        console.error('[Weight] Failed to add entry:', error);
        return;
      }

      setEntries(prev => [data, ...prev]);
      setCurrentWeight(weight);
      Storage.set('currentWeight', weight);
    } catch (err) {
      console.error('[Weight] Error adding entry:', err);
    }
  };

  const addMeasurements = async (measurementData: Partial<BodyMeasurement>) => {
    if (!currentUser || !sb) {
      console.warn('[Weight] Cannot add measurements - no user or sb');
      return;
    }

    try {
      const { data, error } = await sb
        .from('body_measurements')
        .insert({
          user_id: currentUser.id,
          neck_cm: measurementData.neck_cm,
          chest_cm: measurementData.chest_cm,
          waist_cm: measurementData.waist_cm,
          hips_cm: measurementData.hips_cm,
          upper_arms_cm: measurementData.upper_arms_cm,
          thighs_cm: measurementData.thighs_cm,
          recorded_at: measurementData.recorded_at || new Date().toISOString(),
          notes: measurementData.notes || null
        })
        .select()
        .single();

      if (error) {
        console.error('[Weight] Failed to add measurements:', error);
        return;
      }

      setMeasurements(prev => [data, ...prev]);
    } catch (err) {
      console.error('[Weight] Error adding measurements:', err);
    }
  };

  const updateGoal = (weight: number) => {
    setGoalWeight(weight);
    Storage.set('goalWeight', weight);
  };

  const updateUnitPreferences = (prefs: UnitPreferences) => {
    setUnit(prefs.preferred_weight_unit);
    setUnitPreferences(prefs);
  };

  // Helper methods using current unit preference
  const formatWeightHelper = (kg: number | null) => formatWeight(kg, unit);
  const formatWeightWithUnitHelper = (kg: number | null) => formatWeightWithUnit(kg, unit);
  const parseWeightHelper = (str: string) => parseWeight(str, unit);
  const formatMeasurementHelper = (cm: number | null | undefined) => {
    if (cm === null || cm === undefined) return cm;
    return unitPreferences?.preferred_measure_unit === 'in' ? cmToInches(cm) : cm;
  };
  const getDeltaDisplayHelper = (delta: number) => getDeltaDisplay(delta, unit);

  const value: WeightContextType = {
    currentWeight,
    goalWeight,
    startWeight,
    entries,
    measurements,
    addEntry,
    addMeasurements,
    updateGoal,
    unit,
    unitPreferences,
    hasUnitPreferences: !!unitPreferences,
    setUnitPreferences: updateUnitPreferences,
    showHistoricalEntries,
    setShowHistoricalEntries,
    formatWeight: formatWeightHelper,
    formatWeightWithUnit: formatWeightWithUnitHelper,
    parseWeight: parseWeightHelper,
    formatMeasurement: formatMeasurementHelper,
    getDeltaDisplay: getDeltaDisplayHelper
  };

  return <WeightContext.Provider value={value}>{children}</WeightContext.Provider>;
}

export function useWeight() {
  const context = useContext(WeightContext);
  if (context === undefined) {
    throw new Error('useWeight must be used within a WeightProvider');
  }
  return context;
}
